import { apiDeleteSensor } from 'apis/SensorApi';
import FanLoadingIcon from 'components/FanLoadingIcon';
import Icon, { IconList } from 'components/Icon';
import Searchbar from 'components/Searchbar';
import { CURRENT_PAGE_DEFAULT, SENSOR_STATUS_ABNORMAL, SENSOR_STATUS_ALERT, SENSOR_STATUS_ATTENTION, SENSOR_STATUS_GATE_CLOSED, SENSOR_STATUS_GATE_OPENED, SENSOR_STATUS_NORMAL } from 'constants/config';
import { DIALOG_ERROR_GENERAL_ERROR_MESSAGE, DIALOG_MESSAGE_CONFIRM_DELETE_SENSOR, DIALOG_MESSAGE_DELETED_SENSOR_SUCCESSFULLY, TEXT_CREATE_NEW, TEXT_DOWNLOAD_HISTORY, TEXT_SENSOR_CAMERA, TEXT_SENSOR_FLOOD, TEXT_SENSOR_FLOOD_GATE, TEXT_SENSOR_GENERAL, TEXT_SENSOR_LIST, TEXT_SENSOR_MANAGEMENT } from 'constants/message';
import { useDialog } from 'context/DialogContext';
import { useGeneralSetting } from 'context/GeneralSettingContext';
import { useSensorGroups } from 'context/SensorGroupsContext';
import { useSensors } from 'context/SensorsContext';
import { LockState, SensorType } from 'models';
import HistoryPage from 'pages/History/HistoryPage';
import PropTypes from 'prop-types';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { standardizeString } from 'utils/function';
import "./SensorListPage.css";
import SensorDetailList from './components/SensorDetailList';

const propTypes = {
  isManagementPage: PropTypes.bool,
  onFocusOnSensor: PropTypes.func,
  onViewSensor: PropTypes.func,
  onEditSensor: PropTypes.func,
  onSendMail: PropTypes.func,
  sensorType: PropTypes.oneOf([SensorType.FLOOD, SensorType.FLOOD_GATE, SensorType.CAMERA, SensorType.GENERAL]),
  onChangeSensorType: PropTypes.func
};

const defaultProps = {
  isManagementPage: false,
  onFocusOnSensor: (sensor) => { },
  onViewSensor: (sensor) => { },
  onEditSensor: (sensor) => { },
  onSendMail: (sensor) => { },
  sensorType: SensorType.FLOOD,
  onChangeSensorType: (type) => { }
};

const SensorListPage = (props) => {
  /*** States and Variables ***/
  const navigate = useNavigate()
  const [searchedSensors, setSearchedSensors] = React.useState(null);
  const { error, success, warningConfirm } = useDialog();
  const { sensorGroups } = useSensorGroups();
  const [loading, setLoading] = React.useState(false);
  const [curPage, setCurPage] = React.useState(CURRENT_PAGE_DEFAULT);
  const [history, setHistory] = React.useState(false);
  const { sensors } = useSensors();
  const [allSensors, setAllSensors] = React.useState([]);
  const keywordsRef = React.useRef([]);
  const [filterStatus, setFilterStatus] = React.useState('');
  const { generalSetting } = useGeneralSetting();

  /*** Processing ***/
  React.useEffect(() => {
    const targetSensors = [];
    if (!props.isManagementPage) {
      for (const sensor of sensors) {
        const targetGroup = sensorGroups.find(g => g.id === sensor.groupId);
        if (targetGroup?.type === props.sensorType) {
          targetSensors.push(sensor);
        }
      }
      setAllSensors(targetSensors);
    }
    else {
      setAllSensors(sensors);
    }
  }, [props.sensorType, props.isManagementPage, sensors, sensorGroups])

  React.useEffect(() => {
    setSearchedSensors(null);
    setCurPage(1);
    setFilterStatus('');
  }, [props.sensorType, props.isManagementPage])

  const searchSensors = (keywords, status) => {
    keywordsRef.current = keywords;
    const searchResult = [];
    if (props.isManagementPage) {
      for (const sensor of allSensors) {
        const sensorInfo = standardizeString(sensor.managementId) + ' ' +
          standardizeString(sensor.id) + ' ' +
          standardizeString(sensor.apiGroupId) + ' ' +
          standardizeString(sensor.name) + ' ' +
          standardizeString(sensor.location) + ' ' +
          standardizeString(sensor.lat.toString()) + ' ' +
          standardizeString(sensor.lng.toString()) + ' ' +
          standardizeString(sensor.createdAt.split(' ')[0]);
        let accepted = true;
        for (const keyword of keywords) {
          if (!sensorInfo.includes(keyword)) {
            accepted = false;
            break;
          }
        }
        if (accepted) searchResult.push(sensor);
      }
    }
    else {
      for (const sensor of allSensors) {
        const targetGroup = sensorGroups.find(g => g.id === sensor.groupId);
        const messageInfo = targetGroup.messages && targetGroup.messages[sensor.status];
        const sensorInfo = standardizeString(sensor.managementId) + ' ' +
          standardizeString(sensor.name) + ' ' +
          standardizeString(sensor.location) + ' ' +
          standardizeString(sensor.tx) + ' ' +
          (messageInfo ? standardizeString(messageInfo.status) : '') + ' ' +
          (messageInfo ? standardizeString(messageInfo.message) : '') + ' ';
        let accepted = true;
        for (const keyword of keywords) {
          if (!sensorInfo.includes(keyword)) {
            accepted = false;
            break;
          }
        }
        // filter by status
        if (status && messageInfo?.value !== status) {
          accepted = false;
          if (status === SENSOR_STATUS_ALERT && sensor.isLocked === LockState.LOCKED && generalSetting?.alertOn) {
            accepted = true;
          }
        }
        if (accepted) searchResult.push(sensor);
      }
    }
    setSearchedSensors(searchResult);
    setCurPage(1);
  }
  /*** Sub Components ***/
  const renderFunctionButtons = () => {
    return props.sensorType !== SensorType.CAMERA ? <div className='sensor_list_page_right_head_buttons_container'>
      <button
        className='sensor_list_page_button_create button_active'
        onClick={() => { setHistory(true) }}
      >
        {TEXT_DOWNLOAD_HISTORY}
      </button>
      {
        props.isManagementPage && <button
          className='sensor_list_page_button_create button_active'
          onClick={() => { navigate('/create-sensor') }}>
          {TEXT_CREATE_NEW}
        </button>}
    </div> : <></>;
  }
  /*** Event Handlers ***/
  const onDeleteSensor = (item) => {
    if (loading) return;
    warningConfirm(
      DIALOG_MESSAGE_CONFIRM_DELETE_SENSOR,
      async () => {
        setLoading(true);
        const result = await apiDeleteSensor(item.id);
        setLoading(false);
        if (result.success) {
          success(DIALOG_MESSAGE_DELETED_SENSOR_SUCCESSFULLY);
        }
        else {
          error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE);
        }
      });
  }



  const onChangeFilterStatus = (newStatus) => {
    setFilterStatus(newStatus);
    searchSensors(keywordsRef.current, newStatus);
  }
  /*** Main Render ***/
  if (history) {
    return <HistoryPage
      prevPage={props.isManagementPage ? TEXT_SENSOR_MANAGEMENT : TEXT_SENSOR_LIST}
      onClose={() => { setHistory(false) }}
    />
  }

  return <>
    <div className='sensor_list_page_container'>
      <div className='sensor-list_flex-row sensor-list_header'>
        <div className='sensor-list_flex-row header-title'>
          <Icon icon={IconList.iconListing} />
          <label className='header-text'>
            {props.isManagementPage ? TEXT_SENSOR_MANAGEMENT : TEXT_SENSOR_LIST}
          </label>
        </div>
        <div className='sensor-list_flex-row sensor-list-searchbar'>
          <Searchbar
            key={props.sensorType}
            placeholder='機器を検索'
            onSearch={(keywords) => { searchSensors(keywords, filterStatus) }} />
          {!props.isManagementPage && props.sensorType !== SensorType.CAMERA &&
            <select
              className='sensor_list_page_sensor_status_select'
              value={filterStatus}
              onChange={(e) => { onChangeFilterStatus(e.target.value) }}>
              <option value=''>すべてのステータス</option>
              {props.sensorType === SensorType.FLOOD ?
                <>
                  <option value={SENSOR_STATUS_NORMAL}>{SENSOR_STATUS_NORMAL}</option>
                  <option value={SENSOR_STATUS_ATTENTION}>{SENSOR_STATUS_ATTENTION}</option>
                </> :
                <>
                  <option value={SENSOR_STATUS_GATE_OPENED}>{SENSOR_STATUS_GATE_OPENED}</option>
                  <option value={SENSOR_STATUS_GATE_CLOSED}>{SENSOR_STATUS_GATE_CLOSED}</option>
                </>
              }
              <option value={SENSOR_STATUS_ALERT}>{SENSOR_STATUS_ALERT}</option>
              <option value={SENSOR_STATUS_ABNORMAL}>{SENSOR_STATUS_ABNORMAL}</option>
            </select>}
        </div>
      </div>
      <div className='sensor_list_page_detail_list_container'>
        <SensorDetailList
          sensorType={props.sensorType}
          items={searchedSensors || allSensors}
          isManagementList={props.isManagementPage}
          curPage={curPage}
          onEditItem={props.onEditSensor}
          onViewItem={props.onViewSensor}
          onDeleteItem={onDeleteSensor}
          onFocusItem={props.onFocusOnSensor}
          onSendMail={props.onSendMail}
          onPageChange={setCurPage}
          functionBtn={renderFunctionButtons}
        />
        {!props.isManagementPage && <select
          className='sensor_list_page_sensor_type_select'
          value={props.sensorType}
          onChange={(e) => { props.onChangeSensorType(e.target.value) }}>
          <option value={SensorType.CAMERA}>{TEXT_SENSOR_CAMERA}</option>
          <option value={SensorType.FLOOD}>{TEXT_SENSOR_FLOOD}</option>
          <option value={SensorType.FLOOD_GATE}>{TEXT_SENSOR_FLOOD_GATE}</option>
          <option value={SensorType.GENERAL}>{TEXT_SENSOR_GENERAL}</option>
        </select>}
      </div>
      {loading && <FanLoadingIcon size={80} />}
    </div>
  </>
}

SensorListPage.propTypes = propTypes;
SensorListPage.defaultProps = defaultProps;
export default SensorListPage;